import { useState, useRef } from "react";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Camera, UserCircle2, Loader2, User } from "lucide-react";

export default function StaffProfile() {
  const { user } = useAuth();
  const utils = trpc.useUtils();

  const { data: staffCtx, isLoading } = trpc.staff.myContext.useQuery();

  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [photoBase64, setPhotoBase64] = useState<string | null>(null);
  const [photoMime, setPhotoMime] = useState<"image/jpeg" | "image/png" | "image/webp">("image/jpeg");
  const photoInputRef = useRef<HTMLInputElement>(null);

  const uploadPhotoMutation = trpc.staff.uploadPhoto.useMutation({
    onSuccess: (data: any) => {
      toast.success("Foto atualizada com sucesso!");
      utils.staff.myContext.invalidate();
      setPhotoBase64(null);
    },
    onError: (err: any) => toast.error("Erro ao enviar foto", { description: err.message }),
  });

  function handlePhotoChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Foto muito grande. Máximo 5MB."); return; }
    const mime = file.type as "image/jpeg" | "image/png" | "image/webp";
    setPhotoMime(mime);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const b64 = ev.target?.result as string;
      setPhotoBase64(b64);
      setPhotoPreview(b64);
    };
    reader.readAsDataURL(file);
  }

  function handleSavePhoto() {
    if (!photoBase64) return;
    uploadPhotoMutation.mutate({ base64: photoBase64, mimeType: photoMime });
  }

  const currentPhoto = photoPreview ?? (staffCtx as any)?.photoUrl ?? null;
  const staffName = (staffCtx as any)?.staffName ?? user?.name ?? "Funcionário";
  const staffRole = (staffCtx as any)?.staffRole ?? "";

  const roleLabels: Record<string, string> = {
    admin: "Administrador",
    manager: "Gerente",
    staff: "Funcionário",
    cashier: "Caixa",
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-lg mx-auto">
      <div>
        <h1 className="text-xl md:text-2xl font-[Cormorant_Garamond,serif] font-semibold">Meu Perfil</h1>
        <p className="text-sm text-muted-foreground mt-0.5">Atualize sua foto de perfil</p>
      </div>

      <Card className="border-border/50">
        <CardHeader>
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <User className="w-4 h-4 text-primary" />
            Foto de Perfil
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Avatar grande com botão de upload */}
          <div className="flex flex-col items-center gap-4">
            <div
              className="relative group cursor-pointer"
              onClick={() => photoInputRef.current?.click()}
            >
              {currentPhoto ? (
                <img
                  src={currentPhoto}
                  alt={staffName}
                  className="w-28 h-28 rounded-full object-cover border-4 border-border shadow-md"
                />
              ) : (
                <div className="w-28 h-28 rounded-full bg-muted flex items-center justify-center border-4 border-dashed border-border shadow-md">
                  <UserCircle2 className="w-16 h-16 text-muted-foreground" />
                </div>
              )}
              <div className="absolute inset-0 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                <Camera className="w-7 h-7 text-white" />
              </div>
            </div>

            <div className="text-center">
              <p className="font-semibold text-foreground text-lg">{staffName}</p>
              {staffRole && (
                <p className="text-sm text-muted-foreground">{roleLabels[staffRole] ?? staffRole}</p>
              )}
              {user?.email && (
                <p className="text-xs text-muted-foreground mt-0.5">{user.email}</p>
              )}
            </div>

            <input
              ref={photoInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={handlePhotoChange}
            />

            <div className="flex gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => photoInputRef.current?.click()}
              >
                <Camera className="w-4 h-4 mr-1.5" />
                {currentPhoto ? "Trocar foto" : "Adicionar foto"}
              </Button>
              {photoBase64 && (
                <Button
                  size="sm"
                  onClick={handleSavePhoto}
                  disabled={uploadPhotoMutation.isPending}
                  className="bg-primary text-primary-foreground"
                >
                  {uploadPhotoMutation.isPending ? (
                    <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                  ) : null}
                  Salvar foto
                </Button>
              )}
            </div>
          </div>

          <div className="rounded-lg bg-muted/40 border border-border/50 p-4 text-sm text-muted-foreground">
            <p className="font-medium text-foreground mb-1">Dica</p>
            <p>Sua foto aparece no calendário de agendamentos e no site de agendamento online para os clientes. Use uma foto profissional com boa iluminação.</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
